import { Form } from 'antd';
import { ADMIN_ROUTES } from 'constants/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotificationApi } from 'hooks/useNotificationApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useCurrentUserQuery, usePurchaseOrderCreateItemsListQuery, usePurchaseOrderRequestPriceMutation } from 'api';
import { AttachmentItem } from './useAttachments';
import { SupplierItem } from './useSuppliers';

export const useRequestPrice = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const location = useLocation();
  const [isSending, setIsSending] = useState(false);

  const notificationApi = useNotificationApi();
  const { handleSuccess, handleError } = useNotificationMessages();
  const [requestPrice] = usePurchaseOrderRequestPriceMutation();

  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location]);

  const itemIds = useMemo(() => {
    return queryParams.get('items')?.split(',') || [];
  }, [queryParams]);

  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  const userId = JSON.parse(localStorage.getItem('userId') || 'null');
  const { data: currentUser = {} } = useCurrentUserQuery({ userId }, { skip: !userId });

  const { data: purchaseOrderItems } = usePurchaseOrderCreateItemsListQuery({});

  const brandIds = useMemo(() => selectedItems.map((_item: any) => _item.brand.id), [selectedItems]);
  useEffect(() => {
    if (purchaseOrderItems && itemIds.length > 0 && !selectedItems?.length) {
      const filteredItems = purchaseOrderItems.filter((item: any) =>
        item.ids.some((id: string) => itemIds.includes(id))
      );
      setSelectedItems(filteredItems);
    }
  }, [purchaseOrderItems, itemIds, selectedItems]);

  const generateItemsTable = useCallback(() => {
    if (!selectedItems || selectedItems.length === 0) return '';

    let tableHtml = `
    <table style="width:100%; border-collapse:collapse; margin:15px 0;">
      <thead>
        <tr style="background-color:#f5f5f5;">
          <th style="border:1px solid #ccc; padding:8px; text-align:left;">Brand</th>
          <th style="border:1px solid #ccc; padding:8px; text-align:left;">Item SKU</th>
          <th style="border:1px solid #ccc; padding:8px; text-align:center;">Description</th>
          <th style="border:1px solid #ccc; width: 130px; padding:8px; text-align:center;">Quantity</th>
        </tr>
      </thead>
      <tbody>
    `;

    selectedItems.forEach((item) => {
      tableHtml += `
        <tr>
          <td style="border:1px solid #ccc; padding:8px;">${item.brand.name || '-'}</td>
          <td style="border:1px solid #ccc; padding:8px;">${item.itemSku || '-'}</td>
          <td style="border:1px solid #ccc; padding:8px; text-align:center;">${item.description || '-'}</td>
          <td style="border:1px solid #ccc; padding:8px; width: 130px; text-align:center;">${item.quantity || '-'}</td>
        </tr>
      `;
    });

    tableHtml += `
      </tbody>
    </table>
    `;

    return tableHtml;
  }, [selectedItems]);

  const handleSendRequest = useCallback(
    async (
      suppliers: SupplierItem[],
      languageEmailBody: Record<string, any>,
      languageEmailSubject: Record<string, any>,
      attachments: AttachmentItem[]
    ) => {
      if (!currentUser?.email?.trim()) {
        notificationApi({ type: 'error', message: 'Sender email is required' });
        return;
      }

      if (!suppliers?.length) {
        notificationApi({ type: 'error', message: 'At least one supplier must be added' });
        return;
      }

      const missingEmailBodies = suppliers.some((s) => !languageEmailBody[s.languageId]?.trim());
      if (missingEmailBodies) {
        notificationApi({ type: 'error', message: 'Email body is required for each selected language' });
        return;
      }

      setIsSending(true);

      try {
        // Logic for sending the request
        const formData = new FormData();
        formData.append('purchaseOrderItemIds', JSON.stringify(itemIds));
        formData.append('subject', JSON.stringify(languageEmailSubject));
        formData.append('attachmentType', attachments.map((i) => i.type).join(','));
        formData.append('email', currentUser.email);

        attachments.forEach((i) => {
          formData.append('files', i.file as any, i.file.name);
        });
        formData.append('brandIds', JSON.stringify(brandIds));

        formData.append('emailBody', JSON.stringify(languageEmailBody));
        formData.append(
          'suppliers',
          JSON.stringify(
            suppliers?.map((i) => ({
              supplierId: i.supplierId,
              contactPersonId: i.contactPersonId,
              languageId: i.languageId,
            }))
          )
        );

        const response: any = await requestPrice(formData);

        if (response?.data?.success) {
          handleSuccess('Request Price was successfully sent.');
          navigate(ADMIN_ROUTES.purchaseOrder.create);
        } else {
          if (response?.error?.data?.data) {
            handleError(response.error);
          } else {
            notificationApi({ type: 'error', message: response?.error?.data?.message || 'Something went wrong!' });
          }
        }
      } catch (err) {
        handleError(err);
      } finally {
        setIsSending(false);
      }
    },
    [currentUser?.email, requestPrice, notificationApi, handleSuccess, handleError, navigate, brandIds, itemIds]
  );

  const handleCancel = useCallback(() => {
    navigate(ADMIN_ROUTES.purchaseOrder.create);
  }, [navigate]);

  return {
    form,
    brandIds,
    itemIds,
    selectedItems,
    currentUser,
    isSending,
    handleSendRequest,
    handleCancel,
    generateItemsTable,
  };
};
